import fs from 'node:fs';
import path from 'node:path';
import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import type Database from 'better-sqlite3';
import {
  BlobVectorStore,
  CachedInferenceService,
  createAIProvider,
  EmbeddingPipeline,
  InferenceCache,
} from '../ai/index.js';
import { SummarizationPipeline } from '../ai/summarization-pipeline.js';
import type { TraceMcpConfig } from '../config.js';
import { loadConfig, loadGlobalConfigRaw } from '../config.js';
import { initializeDatabase } from '../db/schema.js';
import { isCorruptDbError, isSqliteBusyError, repairIndex } from '../db/repair.js';
import { Store } from '../db/store.js';
import { deleteDbFamily } from '../utils/db-family.js';
import { announceDbHolder, releaseDbHoldersForRoot } from '../db-holders.js';
import { ensureGlobalDirs, getDbPath, TOPOLOGY_DB_PATH } from '../global.js';
import { ExtractPool } from '../indexer/extract-pool.js';
import { IndexAbortedError, IndexingPipeline } from '../indexer/pipeline.js';
import { dropTreeCacheScope } from '../parser/tree-cache.js';
import {
  clearProjectReindexCache,
  shouldSkipRecentReindex,
} from '../indexer/recent-reindex-cache.js';
import { FileWatcher } from '../indexer/watcher.js';
import { logger } from '../logger.js';
import { BackgroundLspEnricher } from '../lsp/background-enricher.js';
import { getReindexStats } from './reindex-stats.js';
import {
  clearProjectStopping,
  markProjectStopping,
  REINDEX_DRAIN_TIMEOUT_MS,
  waitForReindexDrain,
} from './reindex-file-handler.js';
import { SqliteTaskCache } from '../pipeline/index.js';
import { PluginRegistry } from '../plugin-api/registry.js';
import { clearServerPid, ProgressState, writeServerPid } from '../progress.js';
import { detectGitWorktree } from '../project-root.js';
import { isDangerousProjectRoot, setupProject } from '../project-setup.js';
import {
  clearPendingReindex,
  descendantExcludeGlobs,
  findEphemeralProjects,
  findOverlappingProjects,
  getProject,
  listProjects,
  MAX_PENDING_REINDEX_ATTEMPTS,
  recordPendingReindexAttempt,
  registeredNestedRoots,
  shouldServeSharedDbReadOnly,
  unregisterProject,
  updateLastIndexed,
} from '../registry.js';
import type { ServerHandle } from '../server/server.js';
import { createServer } from '../server/server.js';
import { SubprojectManager } from '../subproject/manager.js';
import { TopologyStore } from '../topology/topology-db.js';
import { trailingDebounce } from '../util/debounce.js';
import { selectEagerLoadRoots } from './eager-load.js';
import { serializeError } from './log-error.js';
import {
  removeProjectArtifacts,
  type RemoveArtifactsOptions,
  type RemoveArtifactsResult,
} from './project-artifacts.js';
import type { ProjectResourcePool } from './resource-pool.js';

const AI_COALESCE_WAIT_MS = 5_000;

/**
 * Total budget for one project's teardown waits in `stopProject()` (TRA-1017).
 * Field logs showed a full index taking 188s against a 20s shutdown
 * deadline — unbounded awaits overrun it on every cold-project stop.
 *
 * The three waits share ONE deadline instead of three separate caps because
 * they queue on the same two locks: the watcher's op queue (a restart queued
 * ahead of the unsubscribe holds it draining a hung handler) and the
 * pipeline lock (handlers drained by `watcher.drain()` settle only when the
 * aborted index bails). Bounding any one of them alone still wedges behind
 * the others. Worst sequential path (this budget + the 5s bounded
 * `dispose()` drain) is 18s, inside the 20s `DAEMON_SHUTDOWN_DEADLINE_MS`
 * with margin left for the synchronous closes and `httpServer.close()`.
 * `shutdown()` stops projects concurrently, so one slow project cannot eat
 * another's budget.
 */
export const STOP_PROJECT_TEARDOWN_BUDGET_MS = 13_000;

/**
 * How long a project may sit in `starting`/`indexing` before the vitals tick
 * calls it stalled (TRA-1843). Warn-only: a healthy bulk index of a huge
 * monorepo can legitimately run past this, so stalled projects keep their
 * status and their sweep exemption — the line exists so the next idle-stall
 * triages from one log line instead of an 87-minute mystery.
 */
export const INDEXING_STALL_WARN_MS = 15 * 60_000;

/**
 * Minimum gap between two wake-up loads of the same unloaded descendant
 * (TRA-1863). The decision is taken synchronously in the ancestor's watcher
 * callback while the `addProject()` it kicks is still winding through its
 * awaits (config load, DB open) — without this, every debounce batch during
 * continuous editing would queue another full load of the same root.
 */
export const DESCENDANT_WAKE_COOLDOWN_MS = 60_000;

/**
 * Upper bound on descendant wakes kicked by a single watcher batch
 * (TRA-1863). A bulk operation (branch checkout, dependency install) can
 * touch many registered descendants at once; each wake is a full
 * `addProject()` (DB open, watcher, background indexAll), so an unbounded
 * batch would recreate the boot-time thundering herd mid-session. The rest
 * stay deferred — their next change batch (or first query) wakes them.
 */
export const DESCENDANT_WAKE_PER_BATCH = 5;

/**
 * True when `err` is a SQLite FOREIGN KEY constraint failure. Module scope so
 * both the initial-index catch in `addProject()` and the shared
 * `recoverInitialIndex()` ladder can classify retry errors.
 */
function isForeignKeyError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /FOREIGN KEY constraint failed/i.test(msg);
}

/**
 * Canonical in-memory key for the managed-projects map (TRA-1608).
 *
 * The map used to be keyed by the raw caller string, so the same filesystem
 * root added as `/tmp/ws/` and `/tmp/ws` (or via a relative path) produced
 * TWO managed projects — two pipelines, two DB handles and, worst of all,
 * two live `@parcel/watcher` subscriptions on one root. `path.resolve`
 * collapses trailing slashes / `.` / relative spellings to one key, so one
 * filesystem root always means one managed project and one subscription.
 * This mirrors the registry, whose keys are already `path.resolve`d
 * (setupProject), and the `normRoot` convention in subproject-search.
 * (Symlink aliases still resolve to different keys — same as the registry.)
 */
export function managerKey(root: string): string {
  return path.resolve(root);
}

/**
 * Actual map key for `root`, tolerating entries stored under a raw spelling
 * (TRA-1608 follow-up — Windows CI, PR #1286).
 *
 * `addProject()` always inserts under `managerKey()`, but behavioural tests
 * inject fake `ManagedProject`s directly into the map under POSIX fixture
 * strings like `/tmp/proj-a`. On Windows `path.resolve('/tmp/proj-a')` is
 * `D:\tmp\proj-a`, so a normalized-only lookup misses those entries and
 * `stopProject()`/`removeProject()`/`shutdown()` silently no-op — 25 red
 * lifecycle tests on windows-latest, green everywhere else. Prefer the
 * canonical key, fall back to the raw string.
 */
function managedMapKey(projects: Map<string, ManagedProject>, root: string): string | undefined {
  const key = managerKey(root);
  if (projects.has(key)) return key;
  if (projects.has(root)) return root;
  return undefined;
}

/**
 * Await `promise`, giving up after `timeoutMs` (TRA-1017). Returns true when
 * the promise settled in time, false on timeout. A rejection counts as
 * settled — `initialIndexPromise` never rejects by contract (every branch
 * catches into status), and the chain logs its own errors, so there is
 * nothing to re-log here. The timer is always cleared: a fast settle must
 * not hold the event loop open for the remainder of the timeout.
 */
async function waitWithTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<boolean> {
  if (!promise || typeof promise.then !== 'function') return true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const settled = promise.then(
      () => true,
      () => true,
    );
    const timeout = new Promise<false>((resolve) => {
      timer = setTimeout(() => resolve(false), timeoutMs);
    });
    return await Promise.race([settled, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export interface ManagedProject {
  root: string;
  config: TraceMcpConfig;
  /** On-disk index DB this project opened. Also keys its holder marker (TRA-304). */
  dbPath: string;
  db: Database.Database;
  store: Store;
  registry: PluginRegistry;
  progress: ProgressState;
  pipeline: IndexingPipeline;
  watcher: FileWatcher;
  server: McpServer;
  serverHandle: ServerHandle;
  status: 'starting' | 'indexing' | 'ready' | 'error';
  error?: string;
  /**
   * Epoch ms when the project entered `starting`/`indexing` (TRA-1843). Set
   * once at creation in `addProject()` and never updated: a project that only
   * ever sits in `indexing` without its initial promise settling is exactly
   * the idle-stall `warnOnIndexingStalls()` diagnoses. Entries that predate
   * this field (behavioural-test fakes) simply never count as stalled.
   */
  indexingStartedAt?: number;
  cancelDebouncedAI?: () => void;
  /** Aborted during stopProject() so in-flight AI fetches bail instead of
   *  running to completion against a now-disposed Store. */
  aiAbortController?: AbortController;
  /**
   * Aborted during stopProject() so the in-flight initial `indexAll()` stops
   * at its next batch/phase boundary instead of riding a minutes-long run to
   * completion past the daemon's shutdown deadline (TRA-1017). Wired into
   * every `pipeline.indexAll()` this manager starts for the project. Optional
   * like `aiAbortController` — entries that predate it (and behavioural-test
   * fakes injected without one) simply skip the cooperative fast path; the
   * `STOP_PROJECT_INDEX_WAIT_MS` bound below is the guarantee, the abort is
   * the accelerator.
   */
  indexAbortController?: AbortController;
  /**
   * Phase 3 background LSP enricher — runs LSP enrichment scoped to a
   * watcher burst's changed files N seconds after the burst ends. Null
   * when LSP is disabled in config (the construction is gated on
   * `config.lsp?.enabled`).
   */
  lspEnricher?: BackgroundLspEnricher | null;
  /**
   * Epoch ms of the last request/watcher touch routed to this project.
   * Updated via `ProjectManager.touchActivity()`. Drives the idle-unload
   * sweep — see `project_idle_unload_minutes` config key.
   */
  lastAccessedAt: number;
  /**
   * The fire-and-forget initial `indexAll()` chain (indexing → summarization →
   * embeddings → subproject auto-sync, incl. any FK-recovery retries) kicked
   * off by `addProject()`. Never rejects — every branch of the chain catches
   * its own errors into `managed.status = 'error'` (an abort lands in the
   * `IndexAbortedError` branch instead and leaves the status alone).
   * `stopProject()` aborts the run via `indexAbortController` and then awaits
   * this inside the shared `STOP_PROJECT_TEARDOWN_BUDGET_MS` teardown budget
   * (together with the watcher drain) before closing `managed.db`.
   * Unbounded waiting here overran
   * the 20s shutdown deadline on every cold-project stop (TRA-1017); the
   * bound keeps teardown inside it while the abort keeps the common case
   * fast. A still-open topology.db handle from `runSubprojectAutoSync()` on
   * the timeout path is tolerated: on Windows it can only delay the caller's
   * directory cleanup, and the daemon-wide forced exit remains the backstop.
   */
  initialIndexPromise?: Promise<void>;
}

/**
 * Read-only sweep observability (TRA-1625). Classification of every loaded
 * project with the exact exemptions `unloadIdleProjects` applies, so a daemon
 * that holds far more than `maxLoaded` projects while unloading nothing is
 * diagnosable from the vitals line instead of a guess: `pinned` points at
 * session refcounts that never release, `fresh` at something constantly
 * touching every project, `busy` at wedged indexing.
 */
export interface SweepEligibility {
  loaded: number;
  busy: number;
  pinned: number;
  fresh: number;
  evictable: number;
}

async function runSubprojectAutoSync(projectRoot: string, config: TraceMcpConfig): Promise<void> {
  if (config.topology?.enabled === false) return;
  if (config.topology?.auto_discover === false) return;
  // WHY finally-close: this runs once per addProject() (every register + every
  // daemon-restart reload of every project). Without close() each call leaked a
  // better-sqlite3 handle + fds on the shared topology.db, accumulating over the
  // daemon's lifetime. Mirror of runSubprojectAutoSyncSafe / dropTopologyRows.
  let topoStore: TopologyStore | undefined;
  try {
    ensureGlobalDirs();
    topoStore = new TopologyStore(TOPOLOGY_DB_PATH);
    const manager = new SubprojectManager(topoStore);
    await manager.autoDiscoverSubprojects(projectRoot, {
      contractPaths: config.topology?.contract_globs,
    });
  } catch (err) {
    logger.warn({ error: err, projectRoot }, 'Subproject auto-sync failed (non-fatal)');
  } finally {
    topoStore?.close();
  }
}

/**
 * Minimal in-flight concurrency limiter. Returns a function that wraps an
 * async fn so at most `n` calls run at once; subsequent calls queue.
 * Inlined to avoid pulling p-limit for ~15 LOC. See plan-indexer-perf §2.3.
 * Exported so tests can verify the cap independently of the daemon.
 */
export function pLimit(n: number) {
  let active = 0;
  const queue: Array<() => void> = [];
  // WHY release(): a thrown resolver must not leak the slot — wrap in try/catch.
  const release = () => {
    active--;
    const next = queue.shift();
    if (next) {
      try {
        next();
      } catch {
        /* defensive: a thrown resolver must not leak the slot */
      }
    }
  };
  return async <T>(fn: () => Promise<T>): Promise<T> => {
    if (active >= n) {
      await new Promise<void>((r) => queue.push(r));
    }
    active++;
    try {
      return await fn();
    } finally {
      release();
    }
  };
}

export class ProjectManager {
  private projects = new Map<string, ManagedProject>();
  /** Join callers arriving while setup is still awaiting config or watcher subscription. */
  private pendingAdds = new Map<string, Promise<ManagedProject>>();
  /** Singleton, shared across every managed project. Bounds the daemon's
   *  worker thread count regardless of project count. Lazy-init on the first
   *  addProject() so we can read the project's config for sizing. */
  private sharedPool: ExtractPool | null = null;
  /** Configurable cap on concurrent full indexAll() calls — initial indexing
   *  and watcher rescans alike. Watcher-driven indexFiles() is NOT gated
   *  (small batches). Lazy-init alongside sharedPool. */
  private indexAllLimit: ReturnType<typeof pLimit> | null = null;
  /** Optional shared TopologyStore/DecisionStore pool. When provided,
   *  stopProject() force-disposes the project's pool entry so the SQLite
   *  handles plus their in-memory state don't leak across the daemon's
   *  lifetime. Owned by cli.ts; injected here so tests can verify the wiring
   *  without dragging the HTTP layer in. */
  private resourcePool: ProjectResourcePool | null = null;
  /** Idle-unload sweep timer — see startIdleUnloadSweep(). Null when not running
   *  (never started, or stopped via stopIdleUnloadSweep()/shutdown()). */
  private idleUnloadTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Last wake-up kick per unloaded-descendant root (TRA-1863) — see
   * `DESCENDANT_WAKE_COOLDOWN_MS`. Bounded by the number of registered
   * projects; entries are never cleared (a stale timestamp only delays one
   * wake by the cooldown, it never blocks it).
   */
  private lastDescendantWake = new Map<string, number>();
  /**
   * Latch for the over-ceiling warn in unloadIdleProjects(): a durable
   * all-pinned state would otherwise log the same warn every 5-minute tick
   * (~288 lines/day). Warn once per episode — reset as soon as a sweep ends
   * back under the ceiling (TRA-1738 review).
   */
  private overCapWarned = false;
  /**
   * Latch for `warnOnIndexingStalls()`: roots already warned about in the
   * current stall episode (TRA-1843). Same one-line-per-episode rationale as
   * `overCapWarned` — a wedged project must not log every 60s vitals tick
   * for the rest of the daemon's life. Pruned as soon as a root leaves
   * `starting`/`indexing`, so a re-stall warns again as a new episode.
   */
  private warnedStallRoots = new Set<string>();

  constructor(opts?: { resourcePool?: ProjectResourcePool }) {
    this.resourcePool = opts?.resourcePool ?? null;
  }

  /** Inject the shared resource pool after construction. Used by cli.ts
   *  because the pool is created after ProjectManager (legacy ordering).
   *  Idempotent — second call replaces the reference. */
  setResourcePool(pool: ProjectResourcePool): void {
    this.resourcePool = pool;
  }

  private ensureShared(config: TraceMcpConfig): void {
    if (!this.sharedPool) {
      this.sharedPool = new ExtractPool({
        keepAlive: true,
        size: config.indexer?.workers,
      });
    }
    if (!this.indexAllLimit) {
      this.indexAllLimit = pLimit(config.indexer?.parallel_initial_index ?? 2);
    }
  }

  /** Set up and start indexing for a single project. */
  async addProject(
    projectRoot: string,
    opts?: { watch?: boolean; persist?: boolean },
  ): Promise<ManagedProject> {
    projectRoot = managerKey(projectRoot);
    const pending = this.pendingAdds.get(projectRoot);
    if (pending) return pending;
    const existing = this.projects.get(projectRoot);
    if (existing) return existing;
    const run = this.addProjectOnce(projectRoot, opts);
    this.pendingAdds.set(projectRoot, run);
    try {
      return await run;
    } finally {
      this.pendingAdds.delete(projectRoot);
    }
  }

  private async addProjectOnce(
    projectRoot: string,
    opts?: { watch?: boolean; persist?: boolean },
  ): Promise<ManagedProject> {
    if (!fs.existsSync(projectRoot)) {
      throw new Error(`Project directory does not exist: ${projectRoot}`);
    }
    // Read-mostly mode (a registered subproject served on-demand): index once,
    // no fs watcher, no registry.json / config-file writes. Stays in-memory for
    // the daemon's lifetime but is never restored as a watched project on the
    // next restart — it is re-resolved from topology.db on each connect. Keeps
    // umbrella repos with many subprojects from spawning N watchers (#209).
    const watch = opts?.watch ?? true;
    const persist = opts?.persist ?? true;

    const worktreeInfo = detectGitWorktree(projectRoot);
    const indexRoot = worktreeInfo?.mainRoot ?? projectRoot;

    if (worktreeInfo) {
      logger.info(
        { worktreeRoot: projectRoot, mainRoot: worktreeInfo.mainRoot },
        'Git worktree detected — sharing main repo index',
      );
    }

    // Standard registration: detect, config, DB, registry. Skipped for
    // read-mostly subprojects so they leave no config file in the repo and no
    // registry.json entry — loadConfig() below still yields a valid default
    // config, and the DB is created by initializeDatabase() a few lines down.
    if (persist) setupProject(projectRoot);

    const configResult = await loadConfig(projectRoot);
    if (configResult.isErr()) {
      throw new Error(`Failed to load config for ${projectRoot}: ${configResult.error}`);
    }
    const config = configResult.value;

    // TRA-38: prefer the registry's already-resolved dbPath (set moments ago
    // by setupProject(), which may have reused a same-git-remote sibling's
    // existing DB instead of a fresh one) over recomputing it from scratch.
    // Worktrees are deliberately excluded — they already share one dbPath
    // via `indexRoot` (the main worktree root), a separate, pre-existing
    // mechanism this must not disturb. Read-mostly subprojects (persist
    // false, never registered) simply find nothing and fall through to the
    // same getDbPath() call this line has always made.
    const dbPath = worktreeInfo
      ? getDbPath(indexRoot)
      : (getProject(indexRoot)?.dbPath ?? getDbPath(indexRoot));
    ensureGlobalDirs();

    // TRA-304: this process is about to hold `dbPath` open for the project's
    // whole lifetime — that, not registration, is what makes a sibling
    // checkout's reuse of the same DB concurrent rather than sequential. Cheap
    // and idempotent, so it also covers read-mostly subprojects that never
    // touched the registry.
    try {
      announceDbHolder(dbPath, projectRoot);
    } catch (err) {
      logger.warn({ err, projectRoot, dbPath }, 'failed to announce index-DB holder (non-fatal)');
    }

    // GH#1371 edge 3 / TRA-1916: a follower sharing its owner's DB from a
    // different commit must not write into it (branch-only files would land
    // in the canonical index). Serve the shared DB read-only: no initial
    // index, no file watcher. Worktree-shared roots (indexRoot above) are a
    // separate pre-existing mechanism and are untouched by this verdict.
    const sharedGuard = worktreeInfo
      ? { readOnly: false as const, owner: null, reason: null as string | null }
      : shouldServeSharedDbReadOnly(projectRoot, dbPath);
    if (sharedGuard.readOnly) {
      logger.warn(
        { projectRoot, dbPath, owner: sharedGuard.owner?.root, reason: sharedGuard.reason },
        'Project shares its index DB from a different commit — serving read-only, indexing disabled',
      );
    }

    // TRA-1923: a previous unclean shutdown (overlapping daemon killed
    // mid-bulk-index with synchronous=OFF, force exit with the WAL dirty) can
    // leave the FTS5 index malformed. SQLite reports it on first touch, which
    // may be here at open/migration time — delete the torn family and start
    // clean instead of serving a rotten DB. Exactly one retry; a second
    // corrupt error propagates to the loadAllRegistered failure log.
    const dbOpenOpts = {
      cacheMb: config.index_cache_mb,
      mmapMb: config.index_mmap_mb,
      // TRA-1541: adaptive per-connection memory — steps down on < 6 GiB
      // machines unless the operator pinned `full`.
      memoryProfile: config.index_memory_profile ?? 'auto',
    } as const;
    let db: Database.Database;
    try {
      db = initializeDatabase(dbPath, dbOpenOpts);
    } catch (err) {
      if (!isCorruptDbError(err)) throw err;
      logger.warn(
        { projectRoot, dbPath, error: serializeError(err) },
        'Index DB corrupt at open — deleting the torn family and rebuilding from scratch (TRA-1923)',
      );
      deleteDbFamily(dbPath);
      db = initializeDatabase(dbPath, dbOpenOpts);
    }
    writeServerPid(db);
    const store = new Store(db);
    const registry = PluginRegistry.createWithDefaults();

    this.ensureShared(config);

    const progress = new ProgressState(db, projectRoot);
    // Daemon path: use the SQLite-backed task cache so pass outputs persist on
    // disk and never accumulate in the long-running daemon's heap. The
    // pipeline never owns this cache — the project's `db` does, and is
    // closed by `stopProject()`.
    const taskCache = new SqliteTaskCache(db);
    // Bound pass_cache row age — without this, every fresh (task, input-hash)
    // pair adds one row forever in a long-running daemon. Eviction is a
    // single indexed DELETE backed by idx_pass_cache_created (v28 migration).
    try {
      const ttlDays = config.pipeline?.task_cache_ttl_days ?? 30;
      const removed = taskCache.evictExpired(ttlDays * 86_400_000);
      if (removed > 0) {
        logger.info(
          { projectRoot, removed, ttlDays },
          'pass_cache: evicted expired rows on project start',
        );
      }
    } catch (err) {
      logger.warn(
        { error: serializeError(err), projectRoot },
        'pass_cache: TTL eviction failed (non-fatal)',
      );
    }
    const pipeline = new IndexingPipeline(store, registry, config, projectRoot, progress, {
      extractPool: this.sharedPool,
      taskCache,
    });
    const watcher = new FileWatcher();

    // AI pipelines (optional, lazy): construction is deferred until first use
    // so AI-enabled but never-summarized/embedded projects don't pay the
    // ~50-100 ms per-project setup cost at startup. See plan §5.3.
    const aiEnabled = !!config.ai?.enabled;
    const aiProvider = createAIProvider(config);
    const inferenceCache = aiEnabled ? new InferenceCache(store.db) : null;
    // evictExpired is cheap (single SQL DELETE) — fine to do eagerly.
    inferenceCache?.evictExpired();

    let vectorStore: BlobVectorStore | null = null;
    let embeddingPipeline: EmbeddingPipeline | null = null;
    let summarizationPipeline: SummarizationPipeline | null = null;

    const getVectorStore = (): BlobVectorStore | null => {
      if (!aiEnabled) return null;
      if (!vectorStore) vectorStore = new BlobVectorStore(store.db);
      return vectorStore;
    };

    const getEmbeddingPipeline = (): EmbeddingPipeline | null => {
      if (!aiEnabled) return null;
      if (embeddingPipeline) return embeddingPipeline;
      const vs = getVectorStore();
      if (!vs) return null;
      embeddingPipeline = new EmbeddingPipeline(store, aiProvider.embedding(), vs, progress);
      return embeddingPipeline;
    };

    const getSummarizationPipeline = (): SummarizationPipeline | null => {
      if (!aiEnabled) return null;
      if (config.ai!.summarize_on_index === false) return null;
      if (summarizationPipeline) return summarizationPipeline;
      summarizationPipeline = new SummarizationPipeline(
        store,
        new CachedInferenceService(
          aiProvider.fastInference(),
          inferenceCache!,
          config.ai!.fast_model ?? 'fast',
        ),
        projectRoot,
        {
          batchSize: config.ai!.summarize_batch_size ?? 20,
          kinds: config.ai!.summarize_kinds ?? [
            'class',
            'function',
            'method',
            'interface',
            'trait',
            'enum',
            'type',
          ],
          concurrency: config.ai!.concurrency ?? 1,
          summarizeFromDocstrings: config.ai!.summarizeFromDocstrings,
          maxTokens: config.ai!.summarize_max_tokens,
        },
        progress,
        getVectorStore(),
      );
      return summarizationPipeline;
    };

    // Per-project AbortController. Aborted by stopProject() so in-flight AI
    // fetches bail out instead of running to completion holding references
    // into a Store/ProjectContext the daemon has already disposed.
    const aiAbortController = new AbortController();
    // TRA-1017: separate controller for the indexing pipeline. Aborted by
    // stopProject() alongside the AI one so a minutes-long initial index
    // stops at its next batch/phase boundary instead of outlasting the
    // shutdown deadline. Separate from aiAbortController because the AI
    // pipelines merge per-invocation debounce signals with it, while the
    // index signal is passed to pipeline.indexAll() calls below.
    const indexAbortController = new AbortController();

    const runEmbeddings = (signal?: AbortSignal) => {
      const p = getEmbeddingPipeline();
      if (!p) return;
      // The debounced wrapper passes its per-invocation signal; merge with the
      // project-level signal so either abort short-circuits the pipeline.
      const merged = signal ?? aiAbortController.signal;
      p.indexUnembedded(undefined, merged).catch((err) => {
        logger.error({ error: serializeError(err), projectRoot }, 'Embedding indexing failed');
      });
    };

    const runSummarization = (signal?: AbortSignal) => {
      const p = getSummarizationPipeline();
      if (!p) return;
      const merged = signal ?? aiAbortController.signal;
      p.summarizeUnsummarized(merged).catch((err) => {
        logger.error({ error: serializeError(err), projectRoot }, 'Summarization failed');
      });
    };

    // Use closure refs so the debounced fn can read its own current .signal
    // for the in-flight invocation (it's swapped on every fire).
    let summarizeRef: ReturnType<typeof trailingDebounce> | null = null;
    let embedRef: ReturnType<typeof trailingDebounce> | null = null;
    const debouncedSummarize = trailingDebounce(
      () => runSummarization(summarizeRef?.signal),
      AI_COALESCE_WAIT_MS,
    );
    summarizeRef = debouncedSummarize;
    const debouncedEmbed = trailingDebounce(
      () => runEmbeddings(embedRef?.signal),
      AI_COALESCE_WAIT_MS,
    );
    embedRef = debouncedEmbed;

    // Phase 3 background LSP enricher — only constructed when LSP is enabled
    // in config (opt-in by design). Watcher onChanges feeds it the changed
    // file IDs from each indexFiles() result; the enricher debounces the
    // burst and runs a scoped LSP enrichment off the hot path. See
    // src/lsp/background-enricher.ts.
    const lspEnricher: BackgroundLspEnricher | null = config.lsp?.enabled
      ? new BackgroundLspEnricher({ store, config, rootPath: projectRoot })
      : null;

    // TRA-938: route through the shared pool so this server's TopologyStore/
    // DecisionStore/StateEngine are the daemon-wide singletons, not private
    // per-project SQLite connections onto the same three files.
    const serverHandle = createServer(
      store,
      registry,
      config,
      projectRoot,
      progress,
      // TRA-951: the daemon's per-project server serves every session and the
      // cross-project relay — the preset belongs to the client session and is
      // applied by the proxy, not here.
      {
        ...this.resourcePool?.getSharedDeps(config),
        serveFullSurface: true,
        skipUsagePing: true,
      },
    );

    const managed: ManagedProject = {
      root: projectRoot,
      config,
      dbPath,
      db,
      store,
      registry,
      progress,
      pipeline,
      watcher,
      server: serverHandle.server,
      serverHandle,
      status: 'starting',
      aiAbortController,
      indexAbortController,
      lspEnricher,
      lastAccessedAt: Date.now(),
      indexingStartedAt: Date.now(),
      cancelDebouncedAI: () => {
        debouncedSummarize.cancel();
        debouncedEmbed.cancel();
      },
    };

    this.projects.set(projectRoot, managed);
    // TRA-1553: a previous stop may have thrown midway and left a stale
    // stopping mark behind — the project is servable again from here.
    clearProjectStopping(projectRoot);

    if (!fs.existsSync(projectRoot)) {
      await this.removeProject(projectRoot);
      throw new Error(`Project directory was deleted during setup: ${projectRoot}`);
    }

    // Start indexing in background, gated by the shared semaphore so adding
    // N projects at once doesn't fan out to N concurrent indexAll runs.
    managed.status = 'indexing';
    // Wrap initial indexAll in an FK-auto-recovery retry. When upgrading from
    // an older schema (e.g. v26 -> v28) the existing DB may carry stale rows
    // that were tolerated under the previous edge-resolution algorithm but
    // violate FK constraints under the new one. Re-running with force=true
    // wipes the symbol/edge tables and rebuilds them in correct order from
    // source files, which clears the orphan rows. We only retry ONCE and we
    // only retry for FK errors — every other failure surfaces immediately so
    // we don't mask real bugs. (TRA-1923: SQLITE_CORRUPT gets the same ladder
    // via recoverInitialIndex — see the catch below.)
    // Lazy post-update reindex: if updater.ts stamped pendingReindexForVersion
    // on this project's registry entry, run the initial indexAll with force=true
    // (full rebuild) instead of the cheap incremental path. Clears the flag on
    // success so subsequent restarts go through the fast path. This decouples
    // "trace-mcp version bump" from "reindex storm in post-update migrations",
    // which used to block the event loop long enough that the desktop app's
    // /health watchdog shot the daemon with `daemon restart`. See updater.ts.
    // TRA-274: the flag used to be cleared only on SUCCESS. On a machine where
    // many concurrent CLI sessions share one launchd daemon, the mass rebuild
    // starves /health, a session restarts the daemon, and no project ever
    // finishes — so every boot force-rebuilt every project again, forever.
    // Burn an attempt BEFORE the rebuild starts and give up past the cap.
    const registryEntry = getProject(projectRoot);
    let needsForcedReindex = registryEntry?.pendingReindexForVersion !== undefined;
    if (needsForcedReindex) {
      const attempt = recordPendingReindexAttempt(projectRoot);
      if (attempt > MAX_PENDING_REINDEX_ATTEMPTS) {
        needsForcedReindex = false;
        logger.warn(
          { projectRoot, forVersion: registryEntry?.pendingReindexForVersion, attempt },
          'Lazy post-update reindex: giving up after repeated unfinished attempts — falling back to incremental index',
        );
        try {
          clearPendingReindex(projectRoot);
        } catch {
          /* non-fatal — worst case we retry the cap check next boot */
        }
      } else {
        logger.info(
          { projectRoot, forVersion: registryEntry?.pendingReindexForVersion, attempt },
          'Lazy post-update reindex: forcing full index rebuild for this project',
        );
      }
    }
    // GH#1371 edge 3 / TRA-1916: a read-only follower serves the owner's DB
    // as-is — no initial index. lastIndexed stays as inherited at
    // registration; stamping it here would claim a write that never ran.
    if (sharedGuard.readOnly) {
      managed.status = 'ready';
      managed.initialIndexPromise = Promise.resolve();
    } else {
      managed.initialIndexPromise = this.indexAllLimit!(() => {
        logger.info(
          { projectRoot, reason: 'project-load', discovery: 'auto', forced: needsForcedReindex },
          'Project indexAll starting',
        );
        return pipeline.indexAll(needsForcedReindex, { signal: indexAbortController.signal });
      })
        .then(async () => {
          managed.status = 'ready';
          updateLastIndexed(projectRoot);
          if (needsForcedReindex) {
            try {
              clearPendingReindex(projectRoot);
            } catch (err) {
              logger.warn(
                { projectRoot, err: String(err) },
                'Lazy post-update reindex: clearing flag failed (non-fatal)',
              );
            }
          }
          runSummarization(aiAbortController.signal);
          runEmbeddings(aiAbortController.signal);
          await runSubprojectAutoSync(projectRoot, config);
          logger.info({ projectRoot }, 'Project indexing complete');
        })
        .catch(async (err) => {
          // TRA-1017: asked to stop mid-index (daemon shutdown, project removal,
          // idle unload) — not a failure. The teardown already in progress owns
          // the DB from here; just note it and leave the status alone so a
          // half-torn-down project is never advertised as errored/ready.
          if (err instanceof IndexAbortedError) {
            logger.info({ projectRoot }, 'Initial indexing aborted by project stop (non-fatal)');
            return;
          }
          const finishRecovery = async (via: string): Promise<void> => {
            managed.status = 'ready';
            updateLastIndexed(projectRoot);
            if (needsForcedReindex) {
              try {
                clearPendingReindex(projectRoot);
              } catch {
                /* non-fatal — flag will retry next startup */
              }
            }
            runSummarization();
            runEmbeddings();
            await runSubprojectAutoSync(projectRoot, config);
            logger.info({ projectRoot }, `Project indexing complete (${via})`);
          };
          if (isForeignKeyError(err)) {
            logger.warn(
              { projectRoot, error: String(err) },
              'Initial indexing hit FOREIGN KEY violation — likely stale data from older schema. Retrying with force=true.',
            );
            await this.recoverInitialIndex(managed, projectRoot, {
              finishRecovery,
              failureKind: 'fk',
              originalError: err,
              retryIndex: () =>
                this.indexAllLimit!(() =>
                  pipeline.indexAll(true, { signal: indexAbortController.signal }),
                ),
            });
            return;
          }
          if (isCorruptDbError(err)) {
            // TRA-1923: torn FTS5 write (overlapping indexer in bulk mode, or
            // an unclean shutdown with synchronous=OFF). Rebuild the FTS
            // families in place, then force-reindex — the programmatic
            // equivalent of deleting + rebuilding the index DB, without
            // closing the shared handle. A failure that survives the rebuild
            // escalates ONCE to a hard table reset; anything beyond that is
            // terminal, so recovery can never loop.
            logger.warn(
              { projectRoot, error: serializeError(err) },
              'Initial indexing hit SQLITE_CORRUPT — rebuilding FTS and retrying with force=true.',
            );
            try {
              repairIndex(db, 'rebuild-fts');
            } catch (repairErr) {
              logger.warn(
                { projectRoot, error: serializeError(repairErr) },
                'FTS rebuild on the corrupt index failed — retrying the force-reindex anyway',
              );
            }
            await this.recoverInitialIndex(managed, projectRoot, {
              finishRecovery,
              failureKind: 'corrupt',
              originalError: err,
              retryIndex: () =>
                this.indexAllLimit!(() =>
                  pipeline.indexAll(true, { signal: indexAbortController.signal }),
                ),
              // The hard reset below wipes every data table but deliberately
              // skips FTS tables — rebuild them from the (now empty) symbols
              // table so no stale FTS rows survive into the final attempt.
              beforeHardReset: () => {
                try {
                  repairIndex(db, 'rebuild-fts');
                } catch (repairErr) {
                  logger.warn(
                    { projectRoot, error: serializeError(repairErr) },
                    'FTS rebuild after hard reset failed (non-fatal — the force-reindex repopulates FTS via triggers)',
                  );
                }
              },
            });
            return;
          }
          if (isSqliteBusyError(err)) {
            // TRA-2257: SQLite busy / database locked during initial reindex
            // (e.g. concurrent indexer, overlapping daemon startup, or WAL checkpoint).
            // Attempt bounded retry with backoff. If it resolves, clears pendingReindexForVersion.
            // If the lock persists, leaves status='error' and the error diagnosable.
            logger.warn(
              { projectRoot, error: serializeError(err) },
              'Initial indexing hit database lock (SQLITE_BUSY) — retrying with bounded backoff.',
            );
            await this.recoverBusyInitialIndex(managed, projectRoot, {
              finishRecovery,
              originalError: err,
              retryIndex: () =>
                this.indexAllLimit!(() =>
                  pipeline.indexAll(needsForcedReindex, { signal: indexAbortController.signal }),
                ),
              signal: indexAbortController.signal,
            });
            return;
          }
          managed.status = 'error';
          managed.error = String(err);
          logger.error({ error: serializeError(err), projectRoot }, 'Initial indexing failed');
        });
    }

    // Start file watcher (skipped in read-mostly mode — see `watch` above).
    // Agent-driven edits still reindex via register_edit / the PostToolUse hook;
    // only external (IDE) edits go unnoticed until the next connect's indexAll.
    // GH#1371 edge 3 / TRA-1916: also skipped for read-only followers — a
    // watcher-driven indexFiles would write the follower's files into the
    // owner's DB just as surely as the initial index above would.
    if (watch && !sharedGuard.readOnly) {
      // Separated from the await below so the failure path adds no
      // indentation churn to the 200-line subscribe arguments (TRA-1843).
      const watcherStarted = watcher.start(
        projectRoot,
        config,
        async (paths) => {
          // A filesystem change is real activity too — keep the idle-unload
          // sweep from evicting a project that's being actively edited, even
          // if no MCP session is connected (e.g. IDE-only editing).
          managed.lastAccessedAt = Date.now();
          // TRA-1863: a multi-root parent intentionally watches its declared
          // children's subtrees too, so events here may belong to a registered
          // descendant that is currently unloaded (deferred at boot, or swept
          // as idle) — its own DB would otherwise rot while this parent stays
          // fresh and queries keep binding to the stale child. Wake it.
          this.wakeUnloadedDescendants(projectRoot, paths);
          const watchStart = performance.now();
          const stats = getReindexStats();
          // Dedup against the recent-reindex cache: if the same Edit fired
          // both parcel-watcher and the PostToolUse hook (or register_edit),
          // the second arrival is a no-op. Compute a POSIX-relative key
          // matching the form used by reindex-file-handler.ts.
          const toRel = (p: string): string => {
            const rel = path.isAbsolute(p) ? path.relative(projectRoot, p) : p;
            return path.sep === '\\' ? rel.split('\\').join('/') : rel;
          };
          const skipped: string[] = [];
          const toIndex: string[] = [];
          for (const p of paths) {
            const rel = toRel(p);
            if (shouldSkipRecentReindex(projectRoot, rel)) {
              skipped.push(rel);
            } else {
              toIndex.push(p);
            }
          }
          for (const rel of skipped) {
            const elapsedMs = Math.round(performance.now() - watchStart);
            logger.info(
              {
                event: 'reindex-file',
                project: projectRoot,
                path: rel,
                pathSource: 'watcher',
                skippedRecent: true,
                skippedHash: false,
                indexed: 0,
                elapsedMs,
              },
              'reindex-file telemetry',
            );
            stats.record({
              pathSource: 'watcher',
              skippedRecent: true,
              skippedHash: false,
              indexed: 0,
              elapsedMs,
            });
          }
          if (toIndex.length === 0) return;

          let result:
            | { indexed?: number; skipped?: number; durationMs?: number; changedFileIds?: number[] }
            | undefined;
          let watchErr: unknown;
          try {
            // TRA-1017: the stop signal — a batch queued before the stop must
            // not start persisting after it. An abort mid-batch leaves the
            // incomplete-postprocess mark for the next run to repair.
            result = await pipeline.indexFiles(toIndex, { signal: indexAbortController.signal });
          } catch (err) {
            watchErr = err;
            throw err;
          } finally {
            // TRA-935: the batch's wall-clock is mostly time spent waiting for
            // the pipeline lock when several batches land at once. Report the
            // indexing work as `elapsedMs` and the wait as `queuedMs` — summed
            // into one number they showed watcher reindexes taking hours.
            const totalMs = Math.round(performance.now() - watchStart);
            const elapsedMs = result?.durationMs ?? totalMs;
            const queuedMs = Math.max(0, totalMs - elapsedMs);
            const indexed = result?.indexed ?? 0;
            const skippedRows = result?.skipped ?? 0;
            const skippedHash = indexed === 0 && skippedRows > 0;
            // TRA-2095: an aborted batch (project stopped/unloaded, ephemeral
            // root vanished mid-batch) is one expected event, not N per-file
            // failures — field case: 290 error lines for a single vanished
            // /private/tmp root. One warn summary; genuine (non-abort) errors
            // keep the per-file error lines below.
            // NB: the null guard is load-bearing, not style — batches without
            // errors must never resolve the IndexAbortedError binding, because
            // test doubles of pipeline.js routinely omit that re-export
            // (production always provides it via pipeline.ts).
            const batchAborted =
              watchErr !== undefined && watchErr !== null && watchErr instanceof IndexAbortedError;
            if (batchAborted) {
              logger.warn(
                {
                  event: 'reindex-file',
                  project: projectRoot,
                  pathSource: 'watcher',
                  fileCount: toIndex.length,
                  indexed: 0,
                  elapsedMs,
                  err: String(watchErr),
                },
                'reindex-file batch aborted (non-fatal — root stopped or vanished mid-batch)',
              );
            }
            for (const p of toIndex) {
              const relPosix = toRel(p);
              if (watchErr) {
                if (!batchAborted) {
                  logger.error(
                    {
                      event: 'reindex-file',
                      project: projectRoot,
                      path: relPosix,
                      pathSource: 'watcher',
                      skippedRecent: false,
                      skippedHash: false,
                      indexed: 0,
                      elapsedMs,
                      err: watchErr,
                      error: String(watchErr),
                    },
                    'reindex-file telemetry (error)',
                  );
                  // Expected batch aborts have one warn summary above and no
                  // per-file failures in /api/stats or daemon stats.
                  stats.record({
                    pathSource: 'watcher',
                    skippedRecent: false,
                    skippedHash: false,
                    indexed: 0,
                    elapsedMs,
                    error: true,
                  });
                }
              } else {
                logger.info(
                  {
                    event: 'reindex-file',
                    project: projectRoot,
                    path: relPosix,
                    pathSource: 'watcher',
                    skippedRecent: false,
                    skippedHash,
                    indexed,
                    elapsedMs,
                    queuedMs,
                  },
                  'reindex-file telemetry',
                );
                stats.record({
                  pathSource: 'watcher',
                  skippedRecent: false,
                  skippedHash,
                  indexed,
                  elapsedMs,
                  queuedMs,
                });
              }
            }
          }
          debouncedSummarize();
          debouncedEmbed();
          // Phase 3: schedule scoped LSP enrichment off the hot path. Only
          // fires when LSP is enabled in config (lspEnricher is null
          // otherwise) and only for IDs the pipeline actually touched.
          if (lspEnricher && result?.changedFileIds && result.changedFileIds.length > 0) {
            lspEnricher.scheduleEnrichment(result.changedFileIds);
          }
        },
        undefined,
        async (deleted) => {
          pipeline.deleteFiles(deleted);
        },
        {
          descendantExcludeGlobs: descendantExcludeGlobs(projectRoot),
          // Dropped fs events (bulk checkout, install, wake from sleep) leave
          // the index silently stale. indexAll() re-walks the root and is
          // hash-gated, so unchanged files cost a stat+hash, not a reparse.
          // Gated by the same limiter as initial indexing: wake-from-sleep
          // drops events in EVERY registered project at once, and N full
          // re-walks is exactly the load `parallel_initial_index` bounds.
          // The null branch is belt-and-braces, not a live race: shutdown()
          // clears the limiter only after the bounded watcher/index drain in
          // stopProject (TRA-834), and stopProject unsubscribes before
          // draining, so this read cannot observe null today. It stays so a
          // regression in that ordering degrades to an ungated re-walk
          // instead of a TypeError inside a watcher callback.
          onRescan: async () => {
            // TRA-1715: the root may be gone entirely (a finished task's
            // workdir removed, a volume unmounted) — re-walking it is pure
            // waste, and the project must stop advertising `ready`. Skip the
            // walk, mark the failure so /health tells the truth immediately,
            // and unload the project (the watcher stops with it) so no dead
            // root is re-walked on every subsequent drop. Deferred via
            // setImmediate: stopProject() drains FileWatcher.activeRescan,
            // which IS this run — awaiting it here would deadlock. By the
            // next macrotask the rescan promise has settled and the drain
            // is a no-op. The registry row survives, so /health reports the
            // root as `unloaded` (lazy reload on next request, same as the
            // idle-unload path) until arrival/deletion is confirmed.
            if (!fs.existsSync(projectRoot)) {
              logger.warn(
                { projectRoot },
                'Skipping rescan — project root no longer exists on disk',
              );
              const gone = this.projects.get(projectRoot);
              if (gone) {
                gone.status = 'error';
                gone.error = `Project root no longer exists: ${projectRoot}`;
              }
              setImmediate(() => {
                this.stopProject(projectRoot).catch((err) => {
                  logger.warn(
                    { error: serializeError(err), projectRoot },
                    'Unload of missing-root project failed (non-fatal)',
                  );
                });
              });
              return;
            }
            // TRA-1576: force the full walk. The watcher since-query shares
            // the FSEvents backend that just reported dropped events, so its
            // historical answer is suspect for exactly this window.
            // TRA-1017: same stop signal as the initial index — a rescan
            // racing a shutdown must not start an uncancellable run.
            const run = (force = false) => {
              logger.info(
                {
                  projectRoot,
                  reason: 'watcher-dropped-events',
                  discovery: 'full-walk',
                  forced: force,
                },
                'Project indexAll starting',
              );
              return pipeline.indexAll(force, {
                discovery: 'full-walk',
                signal: indexAbortController.signal,
              });
            };
            const limit = this.indexAllLimit;
            const boundedRun = (force = false) => (limit ? limit(() => run(force)) : run(force));
            try {
              try {
                await boundedRun();
              } catch (err) {
                if (!isForeignKeyError(err)) throw err;
                // A dropped-event walk must repair the same stale symbol rows
                // that the initial-index path repairs. Otherwise FileWatcher
                // only logs the failure and leaves a stale project advertised
                // as ready until another drop happens.
                logger.warn(
                  { projectRoot, error: String(err) },
                  'Dropped-event rescan hit FOREIGN KEY violation — retrying with force=true',
                );
                await boundedRun(true);
              }
              if (
                managed.status === 'error' &&
                managed.error?.startsWith('Dropped-event rescan failed:')
              ) {
                managed.status = 'ready';
                managed.error = undefined;
              }
            } catch (err) {
              if (!(err instanceof IndexAbortedError)) {
                managed.status = 'error';
                managed.error = `Dropped-event rescan failed: ${String(err)}`;
              }
              throw err;
            }
          },
        },
      );
      try {
        await watcherStarted;
      } catch (err) {
        // TRA-1843: a failed watcher start (subscribe error, or the native
        // layer wedged past WATCHER_SUBSCRIBE_TIMEOUT_MS) must not leave the
        // half-added entry behind — tear it down and rethrow so the project
        // retries lazily instead of pinning `indexing`/`sweep_busy` forever.
        await this.abortAddProject(managed);
        throw err;
      }
    }

    // A registered ancestor of this new project already has a live watcher
    // whose ignore list was snapshotted before this project existed — it is
    // now stale and would double-watch/double-index everything under
    // `projectRoot` until restarted. Recompute and restart in place (only
    // for ancestors this daemon actually manages in-memory; an ancestor that
    // was never addProject()'d has nothing to restart). Gated on `persist`
    // alone: that's what controls registry membership (setupProject() above)
    // and thus what descendantExcludeGlobs() reports for `projectRoot` — a
    // project can be registered (persist: true) without this call starting a
    // live watcher for it (watch: false), and the ancestor still needs to
    // exclude that subtree either way.
    if (persist) {
      await this.restartManagedAncestorWatchers(projectRoot);
    }

    logger.info({ projectRoot, watch, persist }, 'Project added to daemon');
    return managed;
  }

  /**
   * Restart the watcher of every currently-managed project whose root is a
   * strict ancestor of `changedRoot`, recomputing descendantExcludeGlobs()
   * so the ancestor's ignore list picks up `changedRoot` having just been
   * registered (addProject) or unregistered (removeProject). Cheap and rare:
   * this only runs on registration changes, never on the watcher's hot path.
   */
  private async restartManagedAncestorWatchers(changedRoot: string): Promise<void> {
    for (const managed of this.projects.values()) {
      if (managed.root === changedRoot) continue;
      const rel = path.relative(managed.root, changedRoot);
      const isStrictAncestor = rel !== '' && !rel.startsWith('..') && !path.isAbsolute(rel);
      if (!isStrictAncestor) continue;
      try {
        await managed.watcher.restartWithExcludes(descendantExcludeGlobs(managed.root));
        logger.info(
          { ancestor: managed.root, changedRoot },
          'Restarted ancestor watcher with recomputed descendant excludes',
        );
      } catch (err) {
        logger.warn(
          { error: err, ancestor: managed.root, changedRoot },
          'Failed to restart ancestor watcher after registration change (non-fatal — ' +
            'excludes will still be recomputed on next daemon restart via loadAllRegistered)',
        );
      }
    }
  }

  /**
   * Reload registered descendants whose files just changed under this
   * ancestor's watcher while they have no live project of their own
   * (TRA-1863).
   *
   * A multi-root parent intentionally watches its declared children's
   * subtrees (they are excluded from `descendantExcludeGlobs()`), so its
   * watcher is the only thing that ever sees an IDE-only edit inside an
   * unloaded child: the child was deferred by the eager-load cap at boot or
   * swept as idle, and without this kick nothing would ever reload it — its
   * DB rots while the parent stays fresh and query routing
   * (`resolveDeepestKnownRoot` prefers the deepest root) keeps serving the
   * stale child. The reload is a plain `addProject()` (watcher + background
   * indexAll), identical to the lazy path a first query would take.
   *
   * Fire-and-forget and bounded: at most `DESCENDANT_WAKE_PER_BATCH` loads
   * per batch (bulk operations touching many descendants must not recreate
   * the boot-time herd), and a per-descendant cooldown covers both
   * continuous editing and the in-flight window before the kicked load lands
   * in the map. Non-multi-root ancestors exclude registered descendants at
   * the native level, so they simply never observe such paths and never wake
   * — no double-watch risk either way.
   */
  private wakeUnloadedDescendants(ancestorRoot: string, changedAbsPaths: string[]): void {
    let nested: string[];
    try {
      nested = registeredNestedRoots(ancestorRoot);
    } catch {
      return;
    }
    if (nested.length === 0) return;
    const now = Date.now();
    let woken = 0;
    for (const abs of changedAbsPaths) {
      // Deepest match wins: a path under doubly-nested registrations wakes
      // only the most-specific unloaded owner, mirroring query routing.
      let owner: string | null = null;
      for (const desc of nested) {
        if (desc.length <= (owner?.length ?? -1)) continue;
        const rel = path.relative(desc, abs);
        if (rel === '' || (!rel.startsWith('..') && !path.isAbsolute(rel))) owner = desc;
      }
      if (!owner) continue;
      if (this.projects.has(managerKey(owner))) continue;
      const last = this.lastDescendantWake.get(owner) ?? 0;
      if (now - last < DESCENDANT_WAKE_COOLDOWN_MS) continue;
      if (woken >= DESCENDANT_WAKE_PER_BATCH) {
        logger.debug(
          { ancestor: ancestorRoot, owner },
          'Descendant wake batch cap reached — remaining unloaded descendants stay deferred',
        );
        return;
      }
      this.lastDescendantWake.set(owner, now);
      woken++;
      logger.info(
        { ancestor: ancestorRoot, descendant: owner },
        'Reloading idle-unloaded registered descendant after ancestor watcher saw its files change',
      );
      void this.addProject(owner).catch((err) => {
        logger.warn(
          { error: serializeError(err), descendant: owner },
          'Descendant wake reload failed (non-fatal — next change batch or query retries)',
        );
      });
    }
  }

  /**
   * Shared retry ladder for initial-index failures that are recoverable by
   * rebuilding in place: FOREIGN KEY violations from stale schema rows, and
   * SQLITE_CORRUPT from torn FTS5 writes (TRA-1923).
   *
   * Rung 1 re-runs the force-reindex against the (possibly repaired) tables.
   * A failure of the SAME kind escalates ONCE to {@link hardResetIndexTables}
   * (+ the caller's `beforeHardReset`, e.g. an FTS rebuild) and one final
   * force-reindex. Any other failure — a different error kind, or anything
   * surviving the hard reset — is terminal: `managed.status = 'error'`, no
   * third attempt, so recovery can never loop. (An abort during a retry is a
   * stop, not a failure: handled like the outer IndexAbortedError branch —
   * this promise must never reject, so every path returns.)
   */
  private async recoverInitialIndex(
    managed: ManagedProject,
    projectRoot: string,
    opts: {
      finishRecovery: (via: string) => Promise<void>;
      failureKind: 'fk' | 'corrupt';
      originalError: unknown;
      retryIndex: () => Promise<unknown>;
      /** Extra in-place reset between the hard table wipe and the final attempt. */
      beforeHardReset?: () => void;
    },
  ): Promise<void> {
    const isFk = opts.failureKind === 'fk';
    const kindLabel = isFk ? 'FOREIGN KEY violation' : 'SQLITE_CORRUPT';
    const shortKind = isFk ? 'FK' : 'corruption';
    const isSameFailure = isFk ? isForeignKeyError : isCorruptDbError;
    try {
      await opts.retryIndex();
      await opts.finishRecovery(isFk ? 'force-reindex recovery' : 'corrupt-FTS rebuild recovery');
      return;
    } catch (retryErr) {
      if (retryErr instanceof IndexAbortedError) {
        logger.info({ projectRoot }, 'Force-reindex recovery aborted by project stop (non-fatal)');
        return;
      }
      if (!isSameFailure(retryErr)) {
        managed.status = 'error';
        managed.error = `Force-reindex after ${shortKind} recovery still failed: ${String(retryErr)}`;
        logger.error(
          {
            error: serializeError(retryErr),
            projectRoot,
            originalError: String(opts.originalError),
          },
          'Force-reindex recovery also failed',
        );
        return;
      }
      logger.warn(
        { projectRoot, error: String(retryErr) },
        `In-place force-reindex still hit ${kindLabel} — hard-resetting index tables and rebuilding from scratch.`,
      );
      try {
        this.hardResetIndexTables(managed.store);
        opts.beforeHardReset?.();
        await opts.retryIndex();
        await opts.finishRecovery(
          isFk ? 'force-reindex recovery after hard reset' : 'corruption recovery after hard reset',
        );
        return;
      } catch (hardErr) {
        if (hardErr instanceof IndexAbortedError) {
          logger.info(
            { projectRoot },
            'Force-reindex recovery aborted by project stop (non-fatal)',
          );
          return;
        }
        managed.status = 'error';
        managed.error = `Index rebuild after ${shortKind} hard reset still failed: ${String(hardErr)}`;
        logger.error(
          {
            error: serializeError(hardErr),
            projectRoot,
            originalError: String(opts.originalError),
            retryError: String(retryErr),
          },
          `${isFk ? 'FK' : 'Corruption'} hard-reset recovery also failed — giving up (no further retry)`,
        );
        return;
      }
    }
  }

  /**
   * Bounded retry ladder for initial indexing failures caused by transient
   * database locks (SQLITE_BUSY / database is locked, TRA-2257). If the lock
   * clears during backoff, completes recovery and clears pendingReindexForVersion.
   * If the lock is permanent, leaves the error diagnosable in managed.error
   * and managed.status='error'.
   */
  private async recoverBusyInitialIndex(
    managed: ManagedProject,
    projectRoot: string,
    opts: {
      finishRecovery: (via: string) => Promise<void>;
      originalError: unknown;
      retryIndex: () => Promise<unknown>;
      signal?: AbortSignal;
      maxRetries?: number;
      backoffMs?: number[];
    },
  ): Promise<void> {
    const maxRetries = opts.maxRetries ?? 2;
    const backoff = opts.backoffMs ?? [250, 750];
    let lastErr: unknown = opts.originalError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (opts.signal?.aborted) {
        logger.info({ projectRoot }, 'Busy-lock recovery aborted by project stop (non-fatal)');
        return;
      }
      const delay = backoff[attempt - 1] ?? 1000;
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, delay);
        if (opts.signal) {
          opts.signal.addEventListener(
            'abort',
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        }
      });
      if (opts.signal?.aborted) {
        logger.info({ projectRoot }, 'Busy-lock recovery aborted by project stop (non-fatal)');
        return;
      }
      try {
        await opts.retryIndex();
        await opts.finishRecovery(`busy-lock recovery (attempt ${attempt})`);
        return;
      } catch (retryErr) {
        if (retryErr instanceof IndexAbortedError) {
          logger.info({ projectRoot }, 'Busy-lock recovery aborted by project stop (non-fatal)');
          return;
        }
        lastErr = retryErr;
        if (!isSqliteBusyError(retryErr)) {
          break;
        }
      }
    }

    managed.status = 'error';
    managed.error = `Initial indexing hit database lock that did not resolve: ${String(lastErr)}`;
    logger.error(
      {
        error: serializeError(lastErr),
        projectRoot,
        originalError: String(opts.originalError),
      },
      'Initial indexing busy-lock recovery also failed — giving up (no further retry)',
    );
  }

  /**
   * FK-recovery hard reset. Wipes every graph/data table on the project's
   * INDEX database in place, leaving the schema, seed rows (node_types,
   * edge_types), version tracking (schema_meta, schema_migrations) and the
   * server PID row intact. After this returns the index is genuinely empty
   * (`symbols` has 0 rows), so the follow-up `indexAll(true)` re-enters
   * bulk-load mode — which sets `foreign_keys = OFF` — and rebuilds the graph
   * from scratch instead of colliding with the stale rows that wedged the
   * in-place force-reindex.
   *
   * Why in-place rather than close → delete file → reopen: the project's
   * `store`, `pipeline`, `serverHandle`, watcher callbacks and AI pipelines
   * all capture THIS exact `Store`/`Database` handle by closure. Swapping the
   * on-disk file would require re-wiring every one of them and would race the
   * live watcher. Clearing the tables on the shared handle keeps all wiring
   * valid and needs no lock coordination beyond the pipeline's own `_lock`
   * (held by the caller, which awaits the failed `indexAll` before calling us).
   *
   * SCOPE: this touches ONLY the index DB reachable via `store.db`. The
   * decisions.db and topology.db handles live in a separate resource pool and
   * are never opened here — the FK error is always thrown inside an index
   * write transaction, so the index DB is the only database to rebuild.
   *
   * Idempotent and drift-proof: the table list is enumerated from
   * `sqlite_master` at runtime rather than hard-coded, so a future migration
   * that adds a table is cleared automatically. FTS5 virtual + shadow tables
   * are skipped — they are content-external mirrors of `symbols` and stay
   * consistent through the `symbols_ad` delete trigger.
   */
  private hardResetIndexTables(store: Store): void {
    const db = store.db;
    // Tables that must survive the wipe: seed/reference data the schema
    // depends on, version tracking, and the daemon PID lock row.
    const PRESERVE = new Set([
      'node_types',
      'edge_types',
      'schema_meta',
      'schema_migrations',
      'server_state',
    ]);
    const rows = db
      .prepare(
        `SELECT name FROM sqlite_master
         WHERE type = 'table'
           AND name NOT LIKE 'sqlite_%'
           AND name NOT LIKE '%_fts'
           AND name NOT LIKE '%_fts_%'`,
      )
      .all() as Array<{ name: string }>;
    const targets = rows.map((r) => r.name).filter((name) => !PRESERVE.has(name));

    // FK enforcement OFF for the duration of the wipe so we can clear tables in
    // any order without tripping the very constraint we are recovering from.
    // Restored to ON in the finally block regardless of outcome — leaving a
    // live daemon handle with FK OFF would silently mask real violations.
    db.pragma('foreign_keys = OFF');
    try {
      const wipe = db.transaction(() => {
        for (const name of targets) {
          db.exec(`DELETE FROM "${name}"`);
        }
      });
      wipe();
    } finally {
      db.pragma('foreign_keys = ON');
    }
    // Flush the WAL so the empty state is durable in the main DB file and the
    // next reader/opener doesn't inherit a bloated WAL from the wipe.
    try {
      db.pragma('wal_checkpoint(TRUNCATE)');
    } catch (err) {
      logger.debug({ err }, 'wal_checkpoint after FK hard reset failed (non-fatal)');
    }
  }

  /**
   * Tear down a half-added project after `addProject()` failed past the point
   * where the entry became visible in the map (TRA-1843).
   *
   * Before this, ANY setup failure past `projects.set()` (a wedged native
   * `watcher.subscribe()` most notably) left a ghost behind: status
   * `indexing`, DB open, initial index queued, holder mark announced. The
   * ghost pinned `projects_indexing`/`sweep_busy` forever with zero pipeline
   * output, and — while stuck inside `watcher.start()` — held a
   * `loadAllRegistered` setup slot, so the remaining eager projects never
   * even started and `/health` reported `starting` indefinitely.
   *
   * Unlike `stopProject()`, this never waits on the watcher op queue or the
   * initial-index chain: the watcher never finished starting (a late native
   * subscribe is dropped by the watcher itself), and the queued index
   * observes `indexAbortController` at its next boundary and bails on its
   * own — awaiting either here could re-wedge startup on the same hang this
   * cleans up after. Every step is best-effort so cleanup never throws a
   * second error masking the original setup failure. The on-disk registry row
   * is untouched, so the project retries lazily on its next request (same as
   * the idle-unload path).
   */
  private async abortAddProject(managed: ManagedProject): Promise<void> {
    const root = managed.root;
    try {
      managed.indexAbortController?.abort();
    } catch {
      /* abort() never throws; defensive */
    }
    try {
      managed.cancelDebouncedAI?.();
    } catch {
      /* non-fatal */
    }
    try {
      managed.lspEnricher?.cancel();
    } catch {
      /* non-fatal */
    }
    // Terminal unsubscribe without draining: nothing was ever served, and the
    // op queue below a wedged subscribe is exactly what must not be awaited.
    try {
      await managed.watcher.unsubscribe();
    } catch {
      /* non-fatal — the map entry is dropped below regardless */
    }
    try {
      clearServerPid(managed.db);
    } catch {
      /* non-fatal */
    }
    try {
      managed.serverHandle.dispose();
    } catch {
      /* non-fatal */
    }
    try {
      await managed.server.close();
    } catch {
      /* non-fatal */
    }
    try {
      await managed.pipeline.dispose();
    } catch {
      /* non-fatal */
    }
    try {
      managed.db.close();
    } catch {
      /* non-fatal — worst case the handle is reclaimed with the process */
    }
    try {
      releaseDbHoldersForRoot(root);
    } catch {
      /* non-fatal */
    }
    try {
      this.sharedPool?.dropProject(root);
    } catch {
      /* non-fatal */
    }
    try {
      dropTreeCacheScope(root);
    } catch {
      /* non-fatal */
    }
    try {
      this.resourcePool?.disposeProject(root);
    } catch {
      /* non-fatal */
    }
    if (this.projects.get(root) === managed) this.projects.delete(root);
    clearProjectStopping(root);
    clearProjectReindexCache(root);
    logger.warn(
      { projectRoot: root },
      'Project load failed mid-setup — tore down half-added entry so it retries lazily (TRA-1843)',
    );
  }

  /**
   * Tear down in-memory state for a project (watcher, server, DB) without
   * touching the on-disk registry. Used by both `shutdown()` (graceful
   * daemon restart) and `removeProject()` (explicit user removal).
   */
  private async stopProject(root: string): Promise<void> {
    // TRA-1608: the map is keyed by managerKey() for addProject() entries, but
    // tolerate raw-spelling keys too (see managedMapKey). Teardown below uses
    // the STORED managed.root spelling so collaborators (registry unregister,
    // sharedPool.dropProject, resourcePool.disposeProject — all mocked with
    // exact-string assertions in lifecycle tests) observe the string the entry
    // was registered under. In production managed.root IS the normalized key,
    // so this is identical there.
    const mapKey = managedMapKey(this.projects, root);
    if (!mapKey) return;
    const managed = this.projects.get(mapKey)!;
    root = managed.root;
    // TRA-1553: refuse new single-file reindex work from this point on. This
    // runs synchronously before the first await, so no interleaving can slip
    // a fresh handleReindexFile/register_edit pipeline run in after the mark.
    markProjectStopping(root);
    // Signal every background producer synchronously, before the first await:
    // abort in-flight AI fetches so a long-running summarize batch cannot
    // return after the DB has closed and write into stale references, and
    // cancel the LSP enricher so its run aborts via its AbortSignal. These are
    // non-blocking, so nothing below can starve them.
    managed.aiAbortController?.abort();
    // TRA-1017: stop the initial index at its next batch/phase boundary. A
    // full index of a large cold project measured 188s in the field against
    // a 20s shutdown deadline — without this the await below rides the whole
    // run and the shutdown always overruns.
    managed.indexAbortController?.abort();
    managed.cancelDebouncedAI?.();
    try {
      managed.lspEnricher?.cancel();
    } catch (err) {
      logger.warn(
        { error: err, projectRoot: root },
        'lspEnricher.cancel() failed during stopProject (non-fatal)',
      );
    }
    // Unsubscribe the file watcher BEFORE the waits below, but WITHOUT
    // draining its in-flight handlers yet (TRA-834 + TRA-1017). Draining here
    // is what wedged shutdown: an in-flight handler queued on the pipeline
    // lock behind the minutes-long initial index settles only when that index
    // does — awaiting it first defeats the abort + timeout below. Unsubscribe
    // stops new work at the source — a still-subscribed watcher keeps firing
    // debounced `onChanges` handlers, each starting a fresh indexing run
    // against a Store a sibling stopProject() is closing (field logs showed
    // 12 "database connection is not open" failures after "Daemon shutting
    // down", one project logging five over 27s). The drain joins the shared
    // bounded wait below, after every producer has been cancelled.
    // The acquisition itself is bounded: a watcher restart queued ahead of
    // this holds the op queue draining that same hung handler, so even the
    // unsubscribe cannot be awaited bare.
    const teardownDeadline = Date.now() + STOP_PROJECT_TEARDOWN_BUDGET_MS;
    const remainingMs = (): number => Math.max(0, teardownDeadline - Date.now());
    const unsubscribed = await waitWithTimeout(managed.watcher.unsubscribe(), remainingMs());
    if (!unsubscribed) {
      logger.warn(
        { projectRoot: root, teardownBudgetMs: STOP_PROJECT_TEARDOWN_BUDGET_MS },
        'stopProject: watcher unsubscribe still queued — continuing teardown without it',
      );
    }
    // TRA-1553: single-file reindexes (HTTP handleReindexFile, MCP
    // register_edit) are not part of initialIndexPromise and nothing below
    // awaits them, yet they run transactions against this project's DB across
    // awaits. Drain what started before the mark above — bounded by the
    // shared budget, so a hung reindex still cannot wedge shutdown past its
    // deadline — before the DB closes. Without this, the next synchronous
    // transaction after the close throws "The database connection is not
    // open" from inside an async continuation as an unhandled rejection
    // (seen on overlapping postinstall respawns, where a ~20 s old instance
    // was SIGTERM'd mid-index).
    const drained = await waitForReindexDrain(
      root,
      Math.min(REINDEX_DRAIN_TIMEOUT_MS, remainingMs()),
    );
    if (!drained) {
      logger.warn(
        { projectRoot: root, drainTimeoutMs: REINDEX_DRAIN_TIMEOUT_MS },
        'stopProject: reindex drain timed out — closing DB with work still in flight',
      );
    }
    // Wait for the background initial-index chain (indexAll → summarize/embed →
    // subproject auto-sync) to finish so its topology.db handle is closed
    // before we tear down this project — see initialIndexPromise's doc comment.
    // TRA-1017: bounded by whatever is left of the shared teardown budget,
    // and SHARED with the watcher drain. The abort above makes a cooperative
    // run settle at its next boundary, but a run wedged inside a synchronous
    // phase (a multi-second edge-resolution pass holds the event loop and
    // cannot observe the abort) would otherwise ride out the whole phase and
    // overrun the shutdown deadline — and the watcher drain waits on handlers
    // queued on that same pipeline lock, so bounding the index without the
    // drain (or vice versa) still wedges. Awaiting both together bounds the
    // lock once, not twice. On timeout the teardown below proceeds anyway —
    // the chain never rejects (every branch catches into status), so nothing
    // is lost by detaching; its next DB statement after the close lands in
    // its own error handling.
    {
      const drains: Array<Promise<unknown>> = [managed.watcher.drain()];
      if (managed.initialIndexPromise) drains.push(managed.initialIndexPromise);
      const settled = await waitWithTimeout(Promise.all(drains), remainingMs());
      if (!settled) {
        logger.warn(
          { projectRoot: root, teardownBudgetMs: STOP_PROJECT_TEARDOWN_BUDGET_MS },
          'stopProject: index/watcher drain still running after abort — continuing teardown without it',
        );
      }
    }
    // A drained handler ends by re-arming debouncedSummarize/debouncedEmbed and
    // scheduling LSP enrichment (see the onChanges tail in addProject), and
    // `trailingDebounce` mints a fresh AbortController when it is scheduled
    // after a cancel — so the pre-drain cancels above no longer cover those
    // timers. Cancel once more now that no handler is left to arm another one
    // (TRA-834 — preserved across the TRA-1017 reorder: the drain moved after
    // the unsubscribe, so the second cancel moved after the drain with it).
    managed.cancelDebouncedAI?.();
    try {
      managed.lspEnricher?.cancel();
    } catch (err) {
      logger.warn(
        { error: err, projectRoot: root },
        'lspEnricher.cancel() failed during stopProject (non-fatal)',
      );
    }
    clearServerPid(managed.db);
    managed.serverHandle.dispose();
    await managed.server.close();
    // Dispose the pipeline before the DB closes. With an injected SQLite task
    // cache the dispose call is mostly a no-op (the cache belongs to `db`),
    // but for any per-pipeline in-memory state the call drops references so
    // the heap can shrink between project lifecycles.
    try {
      await managed.pipeline.dispose();
    } catch (err) {
      logger.warn(
        { error: err, projectRoot: root },
        'pipeline.dispose() failed during stopProject',
      );
    }
    managed.db.close();
    // TRA-304: we no longer hold this DB, so a sibling checkout of the same
    // git remote is free to share it again on its next registration.
    releaseDbHoldersForRoot(root);
    this.projects.delete(mapKey);
    clearProjectStopping(root);
    clearProjectReindexCache(root);
    // Evict per-project caches living inside the shared worker pool
    // (FileExtractor + parsed ProjectContext keyed by rootPath). The pool
    // itself stays warm; only the now-stale per-project entries are dropped.
    // The pipeline's rootPath matches the `root` key — workers see this
    // exact string in `req.rootPath`.
    try {
      this.sharedPool?.dropProject(root);
    } catch (err) {
      logger.warn({ error: err, projectRoot: root }, 'sharedPool.dropProject failed (non-fatal)');
    }
    // TRA-1577: same for the main-thread per-file tree-sitter cache (keyed by
    // the same rootPath scope) — otherwise a removed project's trees linger
    // to LRU eviction. Workers drop their own copy via drop_project.
    try {
      dropTreeCacheScope(root);
    } catch (err) {
      logger.warn({ error: err, projectRoot: root }, 'dropTreeCacheScope failed (non-fatal)');
    }
    // Forget this project's own refcount bookkeeping in the resource pool.
    // TRA-938: the pool's TopologyStore/DecisionStore/StateEngine are
    // daemon-wide singletons now, not owned per-project, so this no longer
    // closes any SQLite handle — it only drops the counter used by
    // unloadIdleProjects/sweepEphemeralProjects. Any in-flight
    // session.onclose handler will see a stale entry but its release() call
    // is a no-op (entry already deleted).
    try {
      this.resourcePool?.disposeProject(root);
    } catch (err) {
      logger.warn(
        { error: err, projectRoot: root },
        'resourcePool.disposeProject failed (non-fatal)',
      );
    }
  }

  /**
   * Stop a project, delete its on-disk artifacts, and drop it from the
   * persistent registry.
   *
   * `keepDbFiles: true` skips the index/session/task-cache DB unlinks but
   * still drops topology + decision rows + registry entry. Default is
   * `false` — desktop "delete project" should reclaim disk.
   *
   * Artifact cleanup is best-effort and idempotent: a partial failure on one
   * tier (e.g. topology DB locked by another process) does not block the
   * registry unregister, so the UI never lies about removal.
   */
  async removeProject(
    root: string,
    options?: RemoveArtifactsOptions,
  ): Promise<RemoveArtifactsResult> {
    // TRA-1608: resolve the effective root BEFORE stopProject() deletes the
    // entry — the stored spelling when loaded (keeps registry/artifact calls
    // pass-through for raw-spelling entries), the caller string when never
    // loaded. stopProject() itself tolerates both spellings (managedMapKey).
    const effective = this.projects.get(managedMapKey(this.projects, root) ?? root)?.root ?? root;
    await this.stopProject(root);
    let artifacts: RemoveArtifactsResult;
    try {
      artifacts = removeProjectArtifacts(effective, options);
    } catch (err) {
      // Cleanup is best-effort — never block unregister on a stray fs error.
      logger.warn({ err, projectRoot: effective }, 'removeProjectArtifacts threw (non-fatal)');
      artifacts = {
        deleted: [],
        kept: [],
        freedBytes: 0,
        topology: { subprojects: 0, services: 0 },
        decisions: { decisions: 0, chunks: 0, clusters: 0, memos: 0 },
        failures: [{ tier: 'artifacts', error: String(err) }],
      };
    }
    unregisterProject(effective);
    // Mirror addProject(): a managed ancestor's watcher ignore list may have
    // been scoped around this now-unregistered root and would otherwise stay
    // stale (harmlessly over-excluding) until the next daemon restart.
    // Recompute so the ancestor resumes owning these now-orphaned files.
    await this.restartManagedAncestorWatchers(effective);
    logger.info(
      {
        projectRoot: effective,
        deletedFiles: artifacts.deleted.length,
        freedBytes: artifacts.freedBytes,
        failures: artifacts.failures,
      },
      'Project removed from daemon',
    );
    return artifacts;
  }

  /**
   * Deregister one-shot agent-run checkouts whose run finished more than
   * `ttlHours` ago, and reclaim their index DBs (TRA-335).
   *
   * `prune` can never reclaim these on its own: it reads "root directory still
   * exists" as liveness, and the runtimes that create these workdirs don't
   * delete them when a run ends. `lastIndexed` is no signal either — this
   * daemon keeps reindexing them forever, so they look permanently fresh. Age
   * since registration is the only honest clock for a directory whose whole
   * purpose was one run, which is what `findEphemeralProjects` measures.
   *
   * Same liveness guards as `unloadIdleProjects` for the loaded ones (never
   * touch a project mid-index or with connected clients), and `removeProject`
   * keeps the index DB whenever a sibling checkout still points at it or holds
   * it open. Unloaded candidates are removed directly — `stopProject` no-ops.
   *
   * ponytail: recognizing an ephemeral root is a path heuristic, so this only
   * covers the workdir shapes `findEphemeralProjects` knows. A per-project
   * last-*queried* timestamp would generalize to CI/worktrees/sandboxes; add
   * it when one of those actually shows up, not before.
   */
  async sweepEphemeralProjects(ttlHours = 72): Promise<string[]> {
    const removed: string[] = [];
    for (const candidate of findEphemeralProjects(ttlHours)) {
      const managed =
        this.projects.get(managerKey(candidate.root)) ?? this.projects.get(candidate.root);
      if (managed && (managed.status === 'starting' || managed.status === 'indexing')) continue;
      if ((this.resourcePool?.getRefCount(candidate.root) ?? 0) > 0) continue;
      logger.info(
        { projectRoot: candidate.root, ageHours: Math.round(candidate.ageHours) },
        'Deregistering stale one-shot workdir project',
      );
      await this.removeProject(candidate.root);
      removed.push(candidate.root);
    }
    return removed;
  }

  /** Get a managed project by root path.
   *
   * TRA-2032: symlink aliases (`/tmp/x` vs `/private/tmp/x` on macOS) resolve
   * to different `path.resolve` keys, so the same checkout 404'd when the
   * caller spelled it differently from registration. On an exact miss, probe
   * the realpath spelling too — read-only, exact hits keep priority, insertion
   * keys are unchanged (still `managerKey()`), so no duplicate entries. The
   * scan always runs (no `real === root` shortcut): the hook now posts
   * canonical roots while the map keeps lexical keys, so only a
   * realpath-to-realpath comparison matches every alias combination.
   */
  getProject(root: string): ManagedProject | undefined {
    const direct = this.projects.get(managerKey(root)) ?? this.projects.get(root);
    if (direct) return direct;
    let real: string;
    try {
      real = fs.realpathSync(root);
    } catch {
      return undefined; // nothing on disk to resolve against
    }
    if (real !== root) {
      const byReal = this.projects.get(managerKey(real)) ?? this.projects.get(real);
      if (byReal) return byReal;
    }
    for (const [key, proj] of this.projects) {
      if (key === managerKey(root) || key === root) continue;
      try {
        if (fs.realpathSync(key) === real) return proj;
      } catch {
        /* managed root gone — not a match */
      }
    }
    return undefined;
  }

  /** Get all managed projects. */
  listProjects(): ManagedProject[] {
    return Array.from(this.projects.values());
  }

  /**
   * Sizes of the small daemon-lifetime bookkeeping maps — surfaced via
   * `GET /debug/memory` (TRA-2061). Both are bounded in practice
   * (descendant-wake keys are registered nested roots, stall roots drain on
   * recovery), so these counters are a tripwire, not a breakdown.
   */
  getWakeBookkeepingSizes(): { descendantWakeEntries: number; stallWarnedRoots: number } {
    return {
      descendantWakeEntries: this.lastDescendantWake.size,
      stallWarnedRoots: this.warnedStallRoots.size,
    };
  }

  /**
   * Record a request/watcher touch for `root`, resetting its idle-unload
   * clock. Call this at every point that routes work to a specific project
   * (session connect/close, REST reindex-file, watcher-driven reindex, etc —
   * mirrors the call sites of cli.ts's `pokeActivity`). No-op if the project
   * isn't currently loaded.
   */
  touchActivity(root: string): void {
    const managed = this.getProject(root);
    if (managed) managed.lastAccessedAt = Date.now();
  }

  /**
   * Classify every loaded project with the exact exemptions
   * `unloadIdleProjects` applies. Read-only: never unloads anything.
   * `evictable` is what `unloadIdleProjects(idleMs, maxLoaded)` would unload
   * on this tick — the TTL victims plus the LRU-ceiling victims, mirroring
   * that method's selection exactly (TRA-1738: the old TTL-only count
   * reported `evictable: 0` on a daemon 6 projects over its ceiling, hiding
   * the fact that the ceiling path would have evicted fresh projects).
   * With `idleMs <= 0` and `maxLoaded <= 0` everything neither busy nor
   * pinned counts as `fresh`.
   */
  sweepEligibility(idleMs: number, maxLoaded = 0): SweepEligibility {
    const out: SweepEligibility = { loaded: 0, busy: 0, pinned: 0, fresh: 0, evictable: 0 };
    const now = Date.now();
    const evictableList: ManagedProject[] = [];
    for (const managed of this.projects.values()) {
      out.loaded++;
      if (managed.status === 'starting' || managed.status === 'indexing') {
        out.busy++;
        continue;
      }
      if ((this.resourcePool?.getRefCount(managed.root) ?? 0) > 0) {
        out.pinned++;
        continue;
      }
      evictableList.push(managed);
    }
    // Same selection as unloadIdleProjects below: TTL first (uncapped —
    // every idle project goes regardless of the ceiling), then LRU-first
    // fill up to `overBy` total.
    const candidates = new Set<ManagedProject>();
    if (idleMs > 0) {
      for (const managed of evictableList) {
        if (now - managed.lastAccessedAt >= idleMs) candidates.add(managed);
      }
    }
    if (maxLoaded > 0 && out.loaded > maxLoaded) {
      const overBy = out.loaded - maxLoaded;
      const lruFirst = [...evictableList].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      for (const managed of lruFirst) {
        if (candidates.size >= overBy) break;
        candidates.add(managed);
      }
    }
    out.evictable = candidates.size;
    out.fresh = evictableList.length - candidates.size;
    return out;
  }

  /**
   * Projects stuck in `starting`/`indexing` longer than `thresholdMs`
   * (TRA-1843). Read-only: never changes status or unloads anything — a slow
   * bulk index is indistinguishable from a wedged one at this level, so the
   * stall only ever surfaces as a log line via `warnOnIndexingStalls()`.
   * Entries without `indexingStartedAt` (predate the field) are skipped: with
   * no entry timestamp a stall duration cannot be proven.
   */
  getStalledIndexing(
    thresholdMs: number = INDEXING_STALL_WARN_MS,
    now: number = Date.now(),
  ): Array<{ root: string; status: ManagedProject['status']; stalledMs: number }> {
    const out: Array<{ root: string; status: ManagedProject['status']; stalledMs: number }> = [];
    for (const managed of this.projects.values()) {
      if (managed.status !== 'starting' && managed.status !== 'indexing') continue;
      if (managed.indexingStartedAt === undefined) continue;
      const stalledMs = now - managed.indexingStartedAt;
      if (stalledMs >= thresholdMs)
        out.push({ root: managed.root, status: managed.status, stalledMs });
    }
    return out;
  }

  /**
   * Log one warn per stalled-indexing episode (see `getStalledIndexing`).
   * Called from the daemon vitals tick, so without the latch a wedged project
   * would log every 60s for the daemon's whole life. Returns the currently
   * stalled list for the vitals/health callers that want it.
   */
  warnOnIndexingStalls(thresholdMs: number = INDEXING_STALL_WARN_MS): Array<{
    root: string;
    status: ManagedProject['status'];
    stalledMs: number;
  }> {
    const stalled = this.getStalledIndexing(thresholdMs);
    const stillStalled = new Set(stalled.map((s) => s.root));
    // Episode pruning first: a root that left indexing (ready/error/unloaded)
    // re-arms its warn, so a re-stall reads as a new episode, not silence.
    for (const root of [...this.warnedStallRoots]) {
      if (!stillStalled.has(root)) this.warnedStallRoots.delete(root);
    }
    for (const s of stalled) {
      if (this.warnedStallRoots.has(s.root)) continue;
      this.warnedStallRoots.add(s.root);
      logger.warn(
        {
          projectRoot: s.root,
          status: s.status,
          stalledSec: Math.round(s.stalledMs / 1000),
          thresholdSec: Math.round(thresholdMs / 1000),
        },
        'Project stuck indexing for a long time — still starting/indexing with no completion (TRA-1843)',
      );
    }
    return stalled;
  }

  /**
   * Unload every currently-loaded project idle longer than `idleMs`, except:
   *  - a project still `starting`/`indexing` (unloading mid-index would abort
   *    real work, not reclaim idle memory);
   *  - a project with connected clients/SSE subscribers, per
   *    `resourcePool.getRefCount(root) > 0` — the same client-tracking
   *    `daemon_idle_exit_minutes` relies on, scoped per-project.
   *
   * Unloading calls the same in-memory teardown as `removeProject()`
   * (`stopProject`) WITHOUT touching the on-disk registry, so the project
   * stays listed and is re-added lazily the next time a request for its root
   * arrives (existing `addProject()` cold-start path — 503 + Retry-After
   * while it warms, see cli.ts serve-http Phase 5.1).
   *
   * `maxLoaded > 0` additionally enforces a hard ceiling on how many projects
   * stay resident: anything above it is evicted least-recently-accessed first,
   * regardless of `idleMs`. Without this, `daemon_eager_load_projects` was a
   * startup budget only — lazy loads walked straight past it (TRA-422: an
   * eager-8 daemon sat at 11 loaded projects three minutes after boot, and at
   * a measured ~100 MB resident per loaded project that is ~300 MB nobody
   * capped). Same exemptions as the TTL path apply, so a busy or indexing
   * project is never evicted just to satisfy the ceiling — the ceiling is
   * best-effort: when every project over the cap is busy or serving connected
   * clients the sweep stays over cap and says so loudly (warn below) instead
   * of silently holding 14 projects against a cap of 8 (TRA-1738).
   *
   * Returns the roots that were unloaded (mainly for tests/telemetry).
   */
  async unloadIdleProjects(idleMs: number, maxLoaded = 0): Promise<string[]> {
    if (idleMs <= 0 && maxLoaded <= 0) return [];
    const now = Date.now();
    const evictable: ManagedProject[] = [];
    let busy = 0;
    let pinned = 0;
    for (const managed of this.projects.values()) {
      if (managed.status === 'starting' || managed.status === 'indexing') {
        busy++;
        continue;
      }
      if ((this.resourcePool?.getRefCount(managed.root) ?? 0) > 0) {
        pinned++;
        continue;
      }
      evictable.push(managed);
    }
    const candidates = new Set<string>();
    if (idleMs > 0) {
      for (const managed of evictable) {
        if (now - managed.lastAccessedAt >= idleMs) candidates.add(managed.root);
      }
    }
    if (maxLoaded > 0 && this.projects.size > maxLoaded) {
      const overBy = this.projects.size - maxLoaded;
      const lruFirst = [...evictable].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      for (const managed of lruFirst) {
        if (candidates.size >= overBy) break;
        candidates.add(managed.root);
      }
    }
    for (const root of candidates) {
      logger.info(
        { projectRoot: root, idleMs, maxLoaded },
        'Unloading idle project (stays registered)',
      );
      await this.stopProject(root);
    }
    // Post-sweep size still over the ceiling means the evictable pool ran
    // out: every remaining project is indexing or serving connected clients.
    // Say so loudly — silently holding 14 projects against a cap of 8 is
    // what made the cap look broken (TRA-1738) — but once per episode, not
    // every 5-minute tick for the whole durable all-pinned stretch.
    if (maxLoaded > 0 && this.projects.size > maxLoaded) {
      if (!this.overCapWarned) {
        this.overCapWarned = true;
        logger.warn(
          { loaded: this.projects.size, maxLoaded, busy, pinned },
          'Idle-unload sweep over maxLoaded but nothing evictable — remainder is indexing or serving connected clients',
        );
      }
    } else {
      this.overCapWarned = false;
    }
    return [...candidates];
  }

  /**
   * Start the periodic idle-unload sweep. `intervalMs` defaults to 5 minutes
   * per the design; `idleMs` is `project_idle_unload_minutes * 60_000` (0
   * disables the TTL rule). `opts.maxLoaded` is the LRU ceiling
   * (`daemon_eager_load_projects`, 0 = unlimited). No timer is armed when both
   * are off. Idempotent: calling twice replaces the previous timer. The
   * interval is unref'd so it never keeps the daemon process alive on its own.
   *
   * `onUnloaded` fires after each sweep tick that unloaded at least one
   * project — cli.ts uses it to tear down its per-project bookkeeping
   * (progress listener etc.), which would otherwise pin the unloaded
   * project's ProgressState for the daemon's lifetime.
   */
  startIdleUnloadSweep(
    idleMs: number,
    opts?: {
      intervalMs?: number;
      maxLoaded?: number;
      evictDeleted?: boolean;
      onUnloaded?: (roots: string[]) => void;
    },
  ): void {
    this.stopIdleUnloadSweep();
    const maxLoaded = opts?.maxLoaded ?? 0;
    if (idleMs <= 0 && maxLoaded <= 0) return;
    this.idleUnloadTimer = setInterval(
      () => {
        if (opts?.evictDeleted) {
          this.evictDeletedProjects()
            .then((evicted) => {
              if (evicted.length > 0) opts?.onUnloaded?.(evicted);
            })
            .catch((err) => {
              logger.warn(
                { err: serializeError(err) },
                'Periodic evictDeletedProjects failed (non-fatal)',
              );
            });
        }
        this.unloadIdleProjects(idleMs, maxLoaded)
          .then((roots) => {
            if (roots.length > 0) opts?.onUnloaded?.(roots);
          })
          .catch((err) => {
            logger.warn({ err: serializeError(err) }, 'Idle-unload sweep failed (non-fatal)');
          });
      },
      opts?.intervalMs ?? 5 * 60_000,
    );
    this.idleUnloadTimer.unref?.();
  }

  /** Stop the periodic idle-unload sweep, if running. Safe to call when not running. */
  stopIdleUnloadSweep(): void {
    if (this.idleUnloadTimer) {
      clearInterval(this.idleUnloadTimer);
      this.idleUnloadTimer = null;
    }
  }

  /**
   * Shut down all projects in-memory. Does NOT unregister from the on-disk
   * registry — the daemon may be restarting (e.g. version-mismatch respawn,
   * supervisor relaunch) and must not lose the user's project list.
   */
  async shutdown(): Promise<void> {
    this.stopIdleUnloadSweep();
    const roots = Array.from(this.projects.keys());
    await Promise.all(roots.map((root) => this.stopProject(root)));
    if (this.sharedPool) {
      await this.sharedPool.terminate();
      this.sharedPool = null;
    }
    this.indexAllLimit = null;
    logger.info('ProjectManager shutdown complete');
  }

  /**
   * Scan registered and resident projects for deleted or dangerous folders.
   * Evicts them from memory, removes database and sidecar artifacts,
   * unregisters them from registry.json, and restarts ancestor watchers.
   * Preserves projects on unmounted volumes (where the parent directory is also missing).
   *
   * Returns the list of evicted project roots.
   */
  async evictDeletedProjects(): Promise<string[]> {
    const registered = listProjects();
    const candidateRoots = new Set<string>(registered.map((e) => e.root));
    for (const managed of this.projects.values()) {
      candidateRoots.add(managed.root);
    }

    const evicted: string[] = [];
    for (const root of candidateRoots) {
      const dangerReason = isDangerousProjectRoot(root);
      if (dangerReason) {
        logger.warn({ root, reason: dangerReason }, 'Removing dangerous project from registry');
        try {
          await this.removeProject(root);
          evicted.push(root);
        } catch (err) {
          logger.warn({ root, err: serializeError(err) }, 'Failed to evict dangerous project');
        }
        continue;
      }
      if (!fs.existsSync(root) && fs.existsSync(path.dirname(root))) {
        logger.warn(
          { root },
          'Removing project with missing folder from registry (parent dir exists — looks like deletion, not unmount)',
        );
        try {
          await this.removeProject(root);
          evicted.push(root);
        } catch (err) {
          logger.warn({ root, err: serializeError(err) }, 'Failed to evict deleted project');
        }
      }
    }
    return evicted;
  }

  /** Load all registered projects and start them. */
  async loadAllRegistered(): Promise<void> {
    // Self-heal: evict registry rows that would block startup or cause
    // "Project not found" 404s at runtime:
    //  - dangerous roots (/, $HOME, system dirs) — typically from an MCP client
    //    that spawned trace-mcp with cwd=/, which would walk the entire FS.
    //  - missing folders whose parent still exists — the project was deleted.
    //    The parent-exists check spares projects on unmounted volumes (e.g.
    //    /Volumes/USB/foo when /Volumes/USB itself is also gone): we only
    //    prune when the immediate parent directory is still there, which is
    //    the signature of a user deletion vs. a transient mount.
    const evicted = new Set(await this.evictDeletedProjects());
    const entries = listProjects().filter((e) => !evicted.has(e.root));
    // Overlapping roots (a container folder registered alongside projects
    // inside it) double-index and double-watch the same files — observed in
    // the field as a 2-3× multiplier on every watcher-driven reindex. The
    // daemon still loads them (user's choice), but says so loudly; doctor
    // reports the same pairs with a fix hint.
    for (const o of findOverlappingProjects()) {
      logger.warn(
        { ancestor: o.ancestor.root, descendant: o.descendant.root },
        'Registered project roots overlap — same files are indexed and watched twice. ' +
          'Keep the per-project registrations and `trace-mcp remove` the container root.',
      );
    }
    // TRA-278: loading every registered project costs ~9 MB of live heap each
    // before any code is indexed, so a machine with ~100 registered repos paid
    // multi-GB RSS at every daemon start. Load only the most recently indexed
    // ones; the rest load lazily on first request (same path as idle-unload).
    const cap = loadGlobalConfigRaw().daemon_eager_load_projects;
    const { eager, deferred } = selectEagerLoadRoots(entries, typeof cap === 'number' ? cap : 8);
    if (deferred.length > 0) {
      logger.info(
        { eager: eager.length, deferred: deferred.length },
        'Deferring cold registered projects to lazy load (daemon_eager_load_projects)',
      );
    }
    // Phase 5+7 audit fix: addProject() runs synchronous setup (DB open, plugin
    // registry, watcher start, ~250-500ms each) BEFORE reaching the
    // semaphore-gated indexAll(). Without a gate, N parallel addProject() calls
    // produce a thundering herd of disk I/O at boot. Cap parallel setup at 2.
    const addLimit = pLimit(2);
    const results = await Promise.allSettled(
      eager.map((entry) => addLimit(() => this.addProject(entry.root))),
    );
    for (let i = 0; i < results.length; i++) {
      if (results[i].status === 'rejected') {
        // serializeError extracts message/stack/code from the raw Error.
        // Without it, an Error instance under a non-`err` key gets
        // JSON.stringify'd, dropping the non-enumerable `.message` — and the
        // SQLite error text we actually need for triage is lost.
        const reason = (results[i] as PromiseRejectedResult).reason;
        logger.error(
          {
            projectRoot: eager[i].root,
            dbPath: getDbPath(eager[i].root),
            error: serializeError(reason),
          },
          'Failed to load registered project',
        );
      }
    }
    logger.info(
      { count: this.projects.size, total: entries.length, deferred: deferred.length },
      'Loaded registered projects',
    );
  }
}
